import { useCallback, useEffect, useRef, useState } from 'react';
import { sdk } from 'src/utilities/sdk';
import { ILocAddRemoveDiners } from 'src/types/LineOfCredit';
import {
  pushError,
  pushErrorFromAPI,
  pushSuccess,
  SdkErrorResponse,
} from 'src/utilities/notifications';
import { AsyncTaskStatus, useAsyncTask } from 'src/utilities/useAsyncTask';
import { LONG_SEARCH_DELAY } from 'src/utilities/useSearchHooks';
import { IErrorResponse } from 'src/utilities/shared-types';

export type UseGroupPeopleManagementProps = {
  action: 'add' | 'remove';
  corpId: string;
  groupId: string;
  onSuccess?: () => void;
  onError?: () => void;
};

type UseGroupPeopleManagementReturn = {
  isLoading?: boolean;
  mutate: (
    dinersSelected: string[],
    groupName?: string,
    singleDinerName?: string
  ) => void;
};

export const useGroupPeopleManagement = ({
  action,
  corpId,
  groupId,
  onSuccess,
  onError,
}: UseGroupPeopleManagementProps): UseGroupPeopleManagementReturn => {
  const { status: asyncTaskStatus, startAsyncTask } = useAsyncTask();
  const currentDinersSelectedRef = useRef<string[] | null>(null);
  const currentSingleDinerName = useRef<string | null>(null);
  const currentGroupName = useRef<string | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>();

  const resetState = () => {
    setIsLoading(false);
    currentDinersSelectedRef.current = null;
    currentSingleDinerName.current = null;
    currentGroupName.current = null;
  };

  const handleSuccess = () => {
    const currentDinersSelected = currentDinersSelectedRef.current;
    const peopleWord =
      currentDinersSelected?.length === 1 ? 'person' : 'people';

    let message;

    switch (action) {
      case 'add':
        message = `${currentDinersSelected?.length} ${peopleWord} added to group.`;
        break;

      case 'remove':
        if (
          currentDinersSelected?.length === 1 &&
          currentSingleDinerName.current &&
          currentGroupName.current
        ) {
          message = `${currentSingleDinerName.current} removed from ${currentGroupName.current}`;
        } else if (currentGroupName.current) {
          message = `${currentDinersSelected?.length} ${peopleWord} removed from ${currentGroupName.current}`;
        } else {
          message = `${currentDinersSelected?.length} ${peopleWord} removed from group`;
        }
        break;

      default:
        break;
    }

    pushSuccess(message);

    onSuccess?.();
    resetState();
  };

  const handleError = (error?: IErrorResponse | SdkErrorResponse) => {
    let errorMessage;

    switch (action) {
      case 'add':
        errorMessage =
          'There was a problem adding people to this group. Try again.';
        break;
      case 'remove':
        errorMessage =
          'There was a problem removing people to this group. Try again.';
        break;
      default:
        break;
    }

    if (error) {
      pushErrorFromAPI(error, {
        fallbackMessage: errorMessage,
      });
    } else {
      pushError(errorMessage);
    }

    onError?.();
    resetState();
  };

  const updateGroupPeople = (body: ILocAddRemoveDiners) =>
    sdk.http.put(`/corp-admin/${corpId}/groups/v2/${groupId}/diners`, body);

  const mutate = useCallback<UseGroupPeopleManagementReturn['mutate']>(
    async (
      dinersSelected: string[],
      groupName = null,
      singleDinerName = null
    ) => {
      try {
        setIsLoading(true);
        currentDinersSelectedRef.current = dinersSelected;
        currentGroupName.current = groupName;
        currentSingleDinerName.current = singleDinerName;

        let body;

        switch (action) {
          case 'add':
            body = {
              diners_to_add: dinersSelected,
              diners_to_remove: [],
            };
            break;
          case 'remove':
            body = {
              diners_to_add: [],
              diners_to_remove: dinersSelected,
            };
            break;
          default:
            break;
        }

        await startAsyncTask(() => {
          return updateGroupPeople(body);
        });
      } catch (error) {
        handleError(error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [action]
  );

  const handleAsyncStatus = useCallback(() => {
    switch (asyncTaskStatus) {
      case AsyncTaskStatus.COMPLETE:
        /**
         * The reason for this timeout is that elastic search needs some time to rearrange the indexes
         * after a record is deleted. Without this timeout the search could potentially
         * return outdated results
         */
        setTimeout(() => {
          handleSuccess();
        }, LONG_SEARCH_DELAY);
        break;

      case AsyncTaskStatus.ERROR:
        handleError();
        break;

      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, asyncTaskStatus]);

  useEffect(() => {
    handleAsyncStatus();
  }, [handleAsyncStatus]);

  return {
    isLoading,
    mutate,
  };
};
